/**
 * Thin Telegram WebApp helper. Returns the global `Telegram.WebApp` if
 * present (loaded via <script src="https://telegram.org/js/telegram-web-app.js">).
 */

interface TelegramWebApp {
  initData: string;
  initDataUnsafe: { user?: { id: number; username?: string; first_name?: string } };
  ready: () => void;
  expand: () => void;
  colorScheme: 'light' | 'dark';
  themeParams: Record<string, string | undefined>;
  HapticFeedback?: { impactOccurred: (style: 'light' | 'medium' | 'heavy') => void };
  showAlert?: (message: string, callback?: () => void) => void;
  showConfirm?: (message: string, callback?: (ok: boolean) => void) => void;
  onEvent?: (event: string, handler: () => void) => void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export function tg(): TelegramWebApp | null {
  return window.Telegram?.WebApp ?? null;
}

export function initData(): string {
  return tg()?.initData ?? '';
}

export function tgUserId(): number | null {
  return tg()?.initDataUnsafe.user?.id ?? null;
}

/**
 * Telegram WebApp's `showAlert` works reliably inside the MiniApp; native
 * `window.alert` is silently dropped on mobile clients. Falls back to native
 * for dev/browser usage.
 */
export function showAlert(message: string): Promise<void> {
  return new Promise((resolve) => {
    const t = tg();
    if (t?.showAlert) {
      t.showAlert(message, () => resolve());
    } else {
      window.alert(message);
      resolve();
    }
  });
}

/**
 * Telegram WebApp's `showConfirm`. Same fallback rationale as `showAlert`.
 */
export function showConfirm(message: string): Promise<boolean> {
  return new Promise((resolve) => {
    const t = tg();
    if (t?.showConfirm) {
      t.showConfirm(message, (ok) => resolve(ok));
    } else {
      resolve(window.confirm(message));
    }
  });
}

export type ThemeMode = 'light' | 'dark' | 'system';
const THEME_STORAGE_KEY = 'control:theme';

/**
 * Read the user's theme preference. Default `system` follows Telegram's
 * colorScheme (or dark when running outside Telegram).
 */
export function getThemeMode(): ThemeMode {
  const v = localStorage.getItem(THEME_STORAGE_KEY);
  if (v === 'light' || v === 'dark' || v === 'system') return v;
  return 'system';
}

export function setThemeMode(mode: ThemeMode): void {
  if (mode === 'system') {
    localStorage.removeItem(THEME_STORAGE_KEY);
  } else {
    localStorage.setItem(THEME_STORAGE_KEY, mode);
  }
  applyTelegramTheme();
}

/**
 * Toggle the `dark` class on <html>. Resolution order:
 *   1. Stored override (light or dark)
 *   2. Telegram colorScheme when present
 *   3. Dark by default (browser dev outside Telegram)
 */
export function applyTelegramTheme(): void {
  const mode = getThemeMode();
  let isDark: boolean;
  if (mode === 'light') isDark = false;
  else if (mode === 'dark') isDark = true;
  else {
    const t = tg();
    isDark = t ? t.colorScheme === 'dark' : true;
  }
  document.documentElement.classList.toggle('dark', isDark);
}

/** `#1a1b1c` (or `1a1b1c`) → `H S% L%` (e.g. `220 4% 11%`). */
function hexToHslTriple(hex: string): string | null {
  const clean = hex.replace(/^#/, '');
  if (!/^[0-9a-fA-F]{6}$/.test(clean)) return null;
  const r = parseInt(clean.slice(0, 2), 16) / 255;
  const g = parseInt(clean.slice(2, 4), 16) / 255;
  const b = parseInt(clean.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) * 60;
        break;
      case g:
        h = ((b - r) / d + 2) * 60;
        break;
      case b:
        h = ((r - g) / d + 4) * 60;
        break;
    }
  }
  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}
